import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { DataCardShell } from "../../../../../components/responsive";
import type { LifecycleRetreat } from "../../../../../types/retreatLifecycle";
import { formatRetreatDateRange } from "../../lifecycle/lifecycleUi";
import {
  dashboardContainedButtonSx,
  dashboardOutlinedButtonSx,
} from "../dashboardUi";

export function ClosingRetreatCard({
  r,
  isSuperAdmin,
  canComplete,
  onHistory,
  completeRetreat,
}: {
  r: LifecycleRetreat;
  isSuperAdmin: boolean;
  canComplete: boolean;
  onHistory: () => void;
  completeRetreat: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const tone = r.closingTone === "warning" ? "warning" : "normal";
  const missing = r.closingMissing || [];
  const percent = r.closingPercent ?? 0;

  return (
    <DataCardShell
      title={r.parishName || r.title}
      subtitle={`Retreat: ${formatRetreatDateRange(r.dateFrom, r.dateTo)} · ${
        r.daysSinceRetreat || 0
      } days ago`}
      chips={
        <Chip
          size="small"
          label={tone === "warning" ? "Closing overdue" : "Closing in progress"}
          color={tone === "warning" ? "warning" : "default"}
        />
      }
      footer={
        <Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Closing progress
            </Typography>
            <Typography variant="caption">{percent}%</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={percent}
            color={tone === "warning" ? "warning" : "primary"}
            sx={{ mt: 0.5, height: 8, borderRadius: 8 }}
          />
          <Typography variant="body2" sx={{ mt: 2, fontWeight: 700 }}>
            Remaining items
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {missing.length === 0 && (
              <Typography variant="body2">All closing items complete.</Typography>
            )}
            {missing.map((item: string) => (
              <Typography key={item} variant="body2">
                • {item}
              </Typography>
            ))}
          </Stack>
          <Alert severity="info" sx={{ mt: 2 }}>
            Elevated access safety expiration: {r.accessExpiration || "—"}
          </Alert>
        </Box>
      }
      secondaryActions={
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ width: "100%", "& .MuiButton-root": { flex: { xs: "1 1 auto", sm: "0 0 auto" } } }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={onHistory}
            sx={dashboardOutlinedButtonSx}
          >
            History
          </Button>
          {isSuperAdmin && (
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate("/retreat-closure-list")}
              sx={dashboardContainedButtonSx}
            >
              Closing Tools
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(`/retreat-setup/${r.id}`)}
            sx={dashboardOutlinedButtonSx}
          >
            View Retreat
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              window.open(`/retreatRoster?retreatId=${r.id}`, "_blank")
            }
            sx={dashboardOutlinedButtonSx}
          >
            View Roster
          </Button>
          {isSuperAdmin && (
            <Button
              size="small"
              color="success"
              disabled={!canComplete}
              onClick={completeRetreat}
            >
              Mark Completed
            </Button>
          )}
        </Stack>
      }
    />
  );
}
